import React, { useEffect, useState } from 'react';
import { Table, Switch, Select, Alert, Card, Tag, message, Space, Button } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { commandSettingAPI, botAPI } from '../services/api';

interface CommandDef {
  id: string;
  command: string;
  description: string;
  category: string;
  permission: string;
  enabled: boolean;
}

interface BotOption {
  id: number;
  bot_name: string;
  bot_username: string;
}

const CATEGORY_COLORS: Record<string, string> = {
  init: 'blue',
  account: 'green',
  query: 'orange',
  admin: 'red',
  utility: 'purple',
};

const CATEGORY_NAMES: Record<string, string> = {
  init: '初始化',
  account: '记账管理',
  query: '查询功能',
  admin: '群组管理',
  utility: '工具功能',
};

const PERMISSION_NAMES: Record<string, string> = {
  all: '所有人',
  operator: '操作人',
  inviter: '邀请人',
};

const CommandSettings: React.FC = () => {
  const [commands, setCommands] = useState<CommandDef[]>([]);
  const [bots, setBots] = useState<BotOption[]>([]);
  const [selectedBotId, setSelectedBotId] = useState<number | undefined>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadBots();
  }, []);

  useEffect(() => {
    if (selectedBotId) {
      loadCommands();
    }
  }, [selectedBotId]);

  const loadBots = async () => {
    try {
      const res = await botAPI.getAll();
      const botList = res.data.data || res.data || [];
      setBots(botList);
      if (botList.length > 0) {
        setSelectedBotId(botList[0].id);
      }
    } catch {
      message.error('加载机器人列表失败');
    }
  };

  const loadCommands = async () => {
    if (!selectedBotId) return;
    setLoading(true);
    try {
      const res = await commandSettingAPI.getAll(selectedBotId);
      setCommands(res.data.data || []);
    } catch {
      message.error('加载命令设置失败');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (commandId: string, enabled: boolean) => {
    if (!selectedBotId) return;
    try {
      await commandSettingAPI.setEnabled(selectedBotId, commandId, enabled);
      setCommands(prev =>
        prev.map(cmd =>
          cmd.id === commandId ? { ...cmd, enabled } : cmd
        )
      );
      message.success(`${enabled ? '启用' : '禁用'}成功`);
    } catch {
      message.error('操作失败');
    }
  };

  const columns = [
    {
      title: '命令',
      dataIndex: 'command',
      key: 'command',
      width: 200,
      render: (text: string) => <code>{text}</code>,
    },
    {
      title: '说明',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: '分类',
      dataIndex: 'category',
      key: 'category',
      width: 120,
      render: (cat: string) => (
        <Tag color={CATEGORY_COLORS[cat] || 'default'}>
          {CATEGORY_NAMES[cat] || cat}
        </Tag>
      ),
    },
    {
      title: '权限',
      dataIndex: 'permission',
      key: 'permission',
      width: 100,
      render: (perm: string) => PERMISSION_NAMES[perm] || perm,
    },
    {
      title: '状态',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 100,
      render: (enabled: boolean, record: CommandDef) => (
        <Switch
          checked={enabled}
          onChange={(checked) => handleToggle(record.id, checked)}
          checkedChildren="启用"
          unCheckedChildren="禁用"
        />
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title="命令开关设置"
        extra={
          <Space>
            <Select
              style={{ width: 220 }}
              placeholder="选择机器人"
              value={selectedBotId}
              onChange={setSelectedBotId}
              options={bots.map(b => ({
                value: b.id,
                label: `${b.bot_name} (@${b.bot_username || b.id})`,
              }))}
            />
            <Button icon={<ReloadOutlined />} onClick={loadCommands}>
              刷新
            </Button>
          </Space>
        }
      >
        <Alert
          message="命令开关说明"
          description="禁用某个命令后，机器人在群组中将不再响应该命令。修改后约 1 分钟内生效（缓存刷新）。"
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />

        <Table
          columns={columns}
          dataSource={commands}
          rowKey="id"
          loading={loading}
          pagination={false}
          size="middle"
        />
      </Card>
    </div>
  );
};

export default CommandSettings;
